import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, Package, Calendar, Settings, Mail, Phone, MapPin, Edit2, UserPlus, Activity, BarChart3, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '../ui/ToastContext';
import useSupabase from '../../hooks/useSupabase';
import ViewLayout from '../ui/ViewLayout';
import Button from '../ui/Button';

const OrganizationProfileView = ({ organizationId, onBack, onEditOrganization }) => {
  const { toast } = useToast();
  const { fetchOrganizationDetails } = useSupabase();
  const [organization, setOrganization] = useState(null);
  const [users, setUsers] = useState([]);
  const [props, setProps] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadOrganizationData();
  }, [organizationId]);

  const loadOrganizationData = async () => {
    try {
      setLoading(true);
      const data = await fetchOrganizationDetails(organizationId);
      setOrganization(data.organization);
      setUsers(data.users);
      setProps(data.props);
      setReservations(data.reservations);
    } catch (error) {
      console.error('Error loading organization data:', error);
      toast.error('Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization details...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Organization not found</p>
          <Button onClick={onBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Organizations
          </Button>
        </div>
      </div>
    );
  }

  // Parse contact info
  let contactInfo = {};
  try {
    contactInfo = organization.contact_info ? JSON.parse(organization.contact_info) : {};
  } catch (e) {
    contactInfo = {};
  }

  // Get status details
  const getStatusDetails = (status) => {
    switch (status) {
      case 'active':
        return { icon: CheckCircle, color: 'text-green-600 bg-green-100', label: 'Active' };
      case 'inactive':
        return { icon: AlertCircle, color: 'text-red-600 bg-red-100', label: 'Inactive' };
      case 'pending':
        return { icon: Clock, color: 'text-yellow-600 bg-yellow-100', label: 'Pending' };
      default:
        return { icon: AlertCircle, color: 'text-gray-600 bg-gray-100', label: 'Unknown' };
    }
  };

  const statusDetails = getStatusDetails(organization.status);
  const StatusIcon = statusDetails.icon;

  const stats = [
    <ViewLayout.Stat 
      title="Total Users" 
      value={users.length} 
      icon={Users} 
      color="blue"
      subtitle={`${organization.max_users ? `of ${organization.max_users} max` : 'unlimited'}`}
    />,
    <ViewLayout.Stat 
      title="Props Owned" 
      value={props.length} 
      icon={Package} 
      color="green" 
    />,
    <ViewLayout.Stat 
      title="Recent Reservations" 
      value={reservations.length} 
      icon={Calendar} 
      color="purple" 
    />,
    <ViewLayout.Stat 
      title="Status" 
      value={statusDetails.label}
      icon={StatusIcon} 
      color={organization.status === 'active' ? 'green' : organization.status === 'pending' ? 'yellow' : 'red'}
    />
  ];

  const actions = [
    <Button
      onClick={() => onEditOrganization(organization)}
      className="bg-blue-600 hover:bg-blue-700"
    >
      <Edit2 className="h-4 w-4 mr-2" />
      Edit Details
    </Button>,
    <Button
      onClick={onBack}
      variant="outline"
    >
      <ArrowLeft className="h-4 w-4 mr-2" />
      Back to Organizations
    </Button>
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'activity', label: 'Activity', icon: Activity }
  ];

  return (
    <ViewLayout
      title={organization.name}
      subtitle={`${organization.org_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} • ${organization.tier?.replace(/\b\w/g, l => l.toUpperCase())} Tier`}
      stats={stats}
      actions={actions}
    >
      {/* Organization Header Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Basic Info */}
          <div className="lg:col-span-2">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900">{organization.name}</h1>
                  <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusDetails.color}`}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {statusDetails.label}
                  </div>
                </div>
                {organization.location && (
                  <div className="flex items-center text-gray-600 mb-2">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{organization.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactInfo.email && (
                <div className="flex items-center text-gray-600">
                  <Mail className="h-4 w-4 mr-2" />
                  <a href={`mailto:${contactInfo.email}`} className="hover:text-blue-600">
                    {contactInfo.email}
                  </a>
                </div>
              )}
              {contactInfo.phone && (
                <div className="flex items-center text-gray-600">
                  <Phone className="h-4 w-4 mr-2" />
                  <a href={`tel:${contactInfo.phone}`} className="hover:text-blue-600">
                    {contactInfo.phone}
                  </a>
                </div>
              )}
            </div>

            {contactInfo.address && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-1">Address</div>
                <div className="text-sm text-gray-600">{contactInfo.address}</div>
              </div>
            )}
          </div>

          {/* Organization Details */}
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Organization Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Type:</span>
                  <span className="text-gray-900 font-medium">
                    {organization.org_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Tier:</span>
                  <span className="text-gray-900 font-medium">
                    {organization.tier?.replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Created:</span>
                  <span className="text-gray-900">
                    {new Date(organization.created_at).toLocaleDateString()}
                  </span>
                </div>
                {organization.pickup_days && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Pickup Days:</span>
                    <span className="text-gray-900 text-right">{organization.pickup_days}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        {organization.notes && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Notes</h3>
            <p className="text-sm text-blue-800">{organization.notes}</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Users Summary */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Users className="h-5 w-5" />
                Users ({users.length})
              </h3>
              <Button size="sm" variant="outline">
                <UserPlus className="h-4 w-4 mr-1" />
                Add User
              </Button>
            </div>
            <div className="space-y-3">
              {users.slice(0, 5).map((user) => (
                <div key={user.id} className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{user.full_name || 'Unnamed User'}</div>
                    <div className="text-sm text-gray-500">{user.role?.replace('_', ' ')}</div>
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(user.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
              {users.length > 5 && (
                <div className="text-center pt-2">
                  <Button size="sm" variant="ghost" onClick={() => setActiveTab('users')}>
                    View all {users.length} users
                  </Button>
                </div>
              )}
              {users.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  No users assigned to this organization
                </div>
              )}
            </div>
          </div>

          {/* Recent Props */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Package className="h-5 w-5" />
                Recent Props
              </h3>
            </div>
            <div className="space-y-3">
              {props.slice(0, 5).map((prop) => (
                <div key={prop.id} className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{prop.name}</div>
                    <div className="text-sm text-gray-500">{prop.category}</div>
                  </div>
                  <div className={`text-xs px-2 py-1 rounded-full ${
                    prop.status === 'available' ? 'bg-green-100 text-green-800' :
                    prop.status === 'checked_out' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {prop.status}
                  </div>
                </div>
              ))}
              {props.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  No props owned by this organization
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Organization Users</h3>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{user.full_name || 'Unnamed User'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">{user.role?.replace('_', ' ')}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.location || 'Not specified'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                      <button className="text-red-600 hover:text-red-900">Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No users assigned to this organization
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Activity
          </h3>
          <div className="space-y-4">
            {reservations.map((reservation) => (
              <div key={reservation.id} className="border-l-4 border-blue-200 pl-4 py-2">
                <div className="font-medium text-gray-900">
                  Reservation {reservation.status}
                </div>
                <div className="text-sm text-gray-600">
                  {reservation.props?.length || 0} props • {new Date(reservation.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
            {reservations.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No recent activity
              </div>
            )}
          </div>
        </div>
      )}
    </ViewLayout>
  );
};

export default OrganizationProfileView;